(() => {
  const FAVORITES_KEY = 'npc:favorites';

  const readJson = (key, fallback) => {
    try {
      const value = JSON.parse(localStorage.getItem(key) || 'null');
      return value ?? fallback;
    } catch (_) {
      return fallback;
    }
  };

  const writeJson = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (_) {
      return false;
    }
  };

  class NpcQuickFinder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.form = this.querySelector('[data-npc23-finder-form]');
      this.title = this.querySelector('[data-npc23-finder-title]');
      this.note = this.querySelector('[data-npc23-finder-note]');
      this.progress = this.querySelector('[data-npc232-finder-progress]');
      this.link = this.querySelector('[data-npc23-finder-link]');
      this.youngLabel = this.querySelector('[data-npc23-young-label]');
      this.state = {};
      this.querySelectorAll('[data-npc23-choice]').forEach((button) => {
        button.setAttribute('aria-pressed', 'false');
        button.addEventListener('click', () => this.select(button));
      });
      this.update();
    }

    select(button) {
      const group = button.dataset.group;
      const value = button.dataset.value;
      if (!group || !value) return;
      this.state[group] = value;
      this.querySelectorAll(`[data-npc23-choice][data-group="${group}"]`).forEach((choice) => {
        choice.setAttribute('aria-pressed', String(choice === button));
      });
      this.update();
    }

    update() {
      if (!this.form) return;
      if (this.youngLabel) {
        this.youngLabel.textContent = this.state.animal === 'cat'
          ? 'Kattunge'
          : this.state.animal === 'dog' ? 'Valp' : 'Valp / kattunge';
      }
      const formatChoices = [...this.querySelectorAll('[data-npc23-animal]')];
      formatChoices.forEach((choice) => {
        const allowed = (choice.dataset.npc23Animal || '').split(' ');
        choice.hidden = Boolean(this.state.animal) && !allowed.includes(this.state.animal);
      });
      const currentFormat = formatChoices.find((choice) => choice.dataset.value === this.state.format);
      if (currentFormat?.hidden) {
        this.state.format = '';
        formatChoices.forEach((choice) => {
          choice.setAttribute('aria-pressed', 'false');
        });
      }

      const completed = ['animal', 'stage', 'format'].filter((key) => this.state[key]).length;
      if (this.progress) this.progress.textContent = `${completed} av 3 val`;
      if (completed < 3) {
        if (this.title) this.title.textContent = completed === 0 ? 'Gör dina tre val.' : `${3 - completed} val kvar.`;
        if (this.note) {
          this.note.textContent = completed === 0
            ? 'Vi visar rätt hylla först när djurslag, livsfas och produkttyp är valda.'
            : 'Fortsätt med nästa val. Inget resultat bestäms i förväg.';
        }
        if (this.link) this.link.hidden = true;
        return;
      }

      const animal = this.state.animal === 'cat' ? 'Katt' : 'Hund';
      const stage = this.state.stage === 'young'
        ? (this.state.animal === 'cat' ? 'Kattunge' : 'Valp')
        : this.state.stage === 'senior' ? 'Senior' : 'Vuxen';
      const animalStage = this.state.stage === 'young'
        ? stage
        : `${stage} ${animal.toLowerCase()}`;
      const formats = {
        dry: 'Torrfoder',
        wet: 'Våtfoder',
        treat: 'Godis & belöning',
        litter: 'Kattsand'
      };
      const routes = {
        'dog:dry': '/collections/hund-torrfoder',
        'dog:wet': '/collections/types?q=Hund%20v%C3%A5tfoder',
        'dog:treat': '/collections/hundgodis',
        'cat:dry': '/collections/torrfoder-katt',
        'cat:wet': '/collections/types?q=V%C3%A5tfoder%20Katt',
        'cat:treat': '/collections/kattgodis',
        'cat:litter': '/collections/kattsand'
      };
      if (this.title) this.title.textContent = `${animalStage} · ${formats[this.state.format] || 'Sortiment'}`;
      if (this.note) {
        if (this.state.format === 'litter') {
          this.note.textContent = 'Öppna kattsandshyllan och jämför klumpförmåga, kornstorlek, doft och förpackning.';
        } else if (this.state.stage === 'adult') {
          this.note.textContent = 'Öppna rätt hylla och filtrera vidare efter varumärke, protein och förpackning.';
        } else {
          this.note.textContent = `Vi söker fram alternativ för ${animalStage.toLowerCase()} och vald fodertyp. Kontrollera alltid livsfasen på produktsidan.`;
        }
      }
      if (this.link) {
        const baseRoute = routes[`${this.state.animal}:${this.state.format}`] || '/collections/all';
        if (this.state.stage === 'adult' || this.state.format === 'litter') {
          this.link.href = baseRoute;
        } else {
          const lifeWord = this.state.stage === 'young'
            ? (this.state.animal === 'cat' ? 'kattunge' : 'valp')
            : 'senior';
          const animalWord = this.state.animal === 'cat' ? 'katt' : 'hund';
          const formatWord = formats[this.state.format] || 'foder';
          this.link.href = `/search?q=${encodeURIComponent(`${lifeWord} ${animalWord} ${formatWord}`)}&type=product`;
        }
        this.link.hidden = false;
      }
    }
  }

  class NpcMemberMemory extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.render();
      window.addEventListener('npc:favorites-change', () => this.renderFavorites());
    }

    render() {
      this.renderMatch();
      this.renderFavorites();
    }

    renderMatch() {
      const mount = this.querySelector('[data-npc23-saved-match]');
      if (!mount) return;
      const match = readJson('npc:last-match', null);
      if (!match?.products?.length) return;
      mount.replaceChildren();
      const summary = document.createElement('p');
      summary.className = 'npc23-memory-empty';
      summary.textContent = match.summary || 'Din senast sparade Nordic Match.';
      const products = document.createElement('div');
      products.className = 'npc23-memory-card__products';
      match.products.slice(0, 3).forEach((product) => products.appendChild(this.productLink(product, product.matchLabel || 'Sparat förslag')));
      mount.append(summary, products);
    }

    renderFavorites() {
      const mount = this.querySelector('[data-npc23-saved-products]');
      if (!mount) return;
      const favorites = readJson(FAVORITES_KEY, []);
      if (!Array.isArray(favorites) || !favorites.length) {
        mount.replaceChildren();
        const empty = document.createElement('div');
        empty.className = 'npc23-memory-empty';
        empty.append('Du har inte sparat några produkter ännu.');
        const br = document.createElement('br');
        const link = document.createElement('a');
        link.href = '/collections/all';
        link.textContent = 'Utforska sortimentet →';
        empty.append(br, link);
        mount.appendChild(empty);
        return;
      }
      mount.replaceChildren();
      const products = document.createElement('div');
      products.className = 'npc23-memory-card__products';
      favorites.slice(0, 8).forEach((product) => products.appendChild(this.productLink(product)));
      mount.appendChild(products);
    }

    productLink(product, eyebrow) {
      const link = document.createElement('a');
      link.href = product.url || `/products/${encodeURIComponent(product.handle || '')}`;
      if (product.image) {
        const image = document.createElement('img');
        image.src = product.image;
        image.alt = product.title || '';
        image.loading = 'lazy';
        link.appendChild(image);
      }
      if (eyebrow) {
        const small = document.createElement('small');
        small.textContent = eyebrow;
        link.appendChild(small);
      }
      const title = document.createElement('strong');
      title.textContent = product.title || 'Sparad produkt';
      link.appendChild(title);
      return link;
    }
  }

  class NpcFeedingPlan extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.nameInput = this.querySelector('[data-npc-plan-name]');
      this.dailyInput = this.querySelector('[data-npc-plan-daily]');
      this.weightInput = this.querySelector('[data-npc-plan-weight]');
      this.daysOutput = this.querySelector('[data-npc-plan-days]');
      this.monthlyOutput = this.querySelector('[data-npc-plan-monthly]');
      this.refillOutput = this.querySelector('[data-npc-plan-refill]');
      this.status = this.querySelector('[data-npc-plan-status]');
      const saved = readJson('npc:feeding-plan', null);
      if (saved) {
        if (this.nameInput && saved.name) this.nameInput.value = saved.name;
        if (this.dailyInput && Number(saved.daily)) this.dailyInput.value = saved.daily;
        if (this.weightInput && Number(saved.weight)) this.weightInput.value = saved.weight;
      }
      [this.nameInput, this.dailyInput, this.weightInput].forEach((input) => {
        input?.addEventListener('input', () => this.update());
      });
      this.querySelector('[data-npc-plan-copy]')?.addEventListener('click', () => this.copy());
      this.querySelector('[data-npc-plan-print]')?.addEventListener('click', () => window.print());
      this.update();
    }

    values() {
      return {
        name: (this.nameInput?.value || '').trim(),
        daily: Math.min(2000, Math.max(10, Number(this.dailyInput?.value) || 250)),
        weight: Math.min(30, Math.max(.1, Number(this.weightInput?.value) || 11.4))
      };
    }

    update() {
      const values = this.values();
      const days = Math.max(1, Math.round((values.weight * 1000) / values.daily));
      const monthly = (values.daily * 30 / 1000).toLocaleString('sv-SE', { maximumFractionDigits: 1 });
      const refill = new Date();
      refill.setDate(refill.getDate() + Math.max(1, days - 3));
      const refillText = new Intl.DateTimeFormat('sv-SE', { day: 'numeric', month: 'short' }).format(refill);
      if (this.daysOutput) this.daysOutput.textContent = `${days} dagar`;
      if (this.monthlyOutput) this.monthlyOutput.textContent = `${monthly} kg`;
      if (this.refillOutput) this.refillOutput.textContent = refillText;
      this.querySelectorAll('[data-npc-plan-timeline] article').forEach((row) => {
        const oldAmount = Math.round(values.daily * Number(row.dataset.old || 0));
        const newAmount = Math.round(values.daily * Number(row.dataset.new || 0));
        const oldOutput = row.querySelector('[data-npc-plan-old]');
        const newOutput = row.querySelector('[data-npc-plan-new]');
        if (oldOutput) oldOutput.textContent = `${oldAmount} g gammalt`;
        if (newOutput) newOutput.textContent = `${newAmount} g nytt`;
      });
      writeJson('npc:feeding-plan', values);
    }

    textPlan() {
      const values = this.values();
      const name = values.name || 'Mitt djur';
      const rows = [...this.querySelectorAll('[data-npc-plan-timeline] article')].map((row) => {
        const day = row.querySelector('span')?.textContent?.trim() || '';
        const oldAmount = row.querySelector('[data-npc-plan-old]')?.textContent?.trim() || '';
        const newAmount = row.querySelector('[data-npc-plan-new]')?.textContent?.trim() || '';
        return `${day}: ${oldAmount} + ${newAmount}`;
      });
      return [
        `Foderplan för ${name}`,
        `Dagsmängd enligt etiketten: ${values.daily} g`,
        `Förpackning: ${values.weight.toLocaleString('sv-SE')} kg`,
        '',
        ...rows,
        '',
        'Exempelplan från Nordic Pet House. Följ alltid produktens aktuella utfodringsguide och veterinärens råd.'
      ].join('\n');
    }

    async copy() {
      const text = this.textPlan();
      let copied = false;
      try {
        await navigator.clipboard.writeText(text);
        copied = true;
      } catch (_) {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        copied = document.execCommand('copy');
        textarea.remove();
      }
      if (this.status) this.status.textContent = copied ? 'Planen är kopierad.' : 'Kunde inte kopiera. Markera planen manuellt.';
    }
  }

  const favoriteItems = () => {
    const stored = readJson(FAVORITES_KEY, []);
    return Array.isArray(stored) ? stored : [];
  };

  const syncFavoriteButtons = (scope = document) => {
    const handles = new Set(favoriteItems().map((item) => item.handle));
    scope.querySelectorAll('[data-npc23-favorite]').forEach((button) => {
      const selected = handles.has(button.dataset.productHandle);
      button.setAttribute('aria-pressed', String(selected));
      const label = button.querySelector('span');
      if (label) label.textContent = selected ? 'Sparad' : 'Spara produkten';
      button.setAttribute('aria-label', `${selected ? 'Ta bort' : 'Spara'} ${button.dataset.productTitle || 'produkten'}`);
    });
  };

  const toggleFavorite = (button) => {
    const handle = button.dataset.productHandle;
    if (!handle) return;
    const favorites = favoriteItems();
    const index = favorites.findIndex((item) => item.handle === handle);
    if (index >= 0) {
      favorites.splice(index, 1);
    } else {
      favorites.unshift({
        handle,
        title: button.dataset.productTitle || 'Produkt',
        url: button.dataset.productUrl || `/products/${handle}`,
        image: button.dataset.productImage || ''
      });
    }
    writeJson(FAVORITES_KEY, favorites.slice(0, 24));
    syncFavoriteButtons();
    window.dispatchEvent(new CustomEvent('npc:favorites-change'));
  };

  const setupProductTabs = (scope = document) => {
    scope.querySelectorAll('[data-npc23-product-tab]').forEach((button) => {
      if (button.dataset.ready === 'true') return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => {
        const target = button.dataset.npc23ProductTab;
        const group = button.closest('.npc23-shop');
        group?.querySelectorAll('[data-npc23-product-tab]').forEach((tab) => tab.setAttribute('aria-selected', String(tab === button)));
        group?.querySelectorAll('[data-npc23-product-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.npc23ProductPanel !== target;
        });
      });
    });
  };

  document.addEventListener('click', (event) => {
    const favorite = event.target.closest('[data-npc23-favorite]');
    if (!favorite) return;
    event.preventDefault();
    event.stopPropagation();
    toggleFavorite(favorite);
  });

  const initialize = (scope = document) => {
    syncFavoriteButtons(scope);
    setupProductTabs(scope);
  };

  if (!customElements.get('npc-quick-finder')) customElements.define('npc-quick-finder', NpcQuickFinder);
  if (!customElements.get('npc-member-memory')) customElements.define('npc-member-memory', NpcMemberMemory);
  if (!customElements.get('npc-feeding-plan')) customElements.define('npc-feeding-plan', NpcFeedingPlan);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
